import ArticleLayout from './ArticleLayout';

export default function UpdatedSpreadsheet() {
  return (
    <ArticleLayout
      title="Updated Sugargoo Spreadsheet: New Finds and Fresh Links for June 2026"
      subtitle="Stay ahead with the most recently updated Sugargoo spreadsheet featuring new product additions, refreshed seller links, and improved category organization. Our June 2026 update brings hundreds of new verified products across all major categories."
      category="Latest Update"
      breadcrumbs={[
        { name: 'Home', path: '/' },
        { name: 'Updated Sugargoo Spreadsheet', path: '/updated-sugargoo-spreadsheet' },
      ]}
      schemaJson={{
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Updated Sugargoo Spreadsheet: New Finds and Fresh Links for June 2026',
        description: 'The latest June 2026 update to the Sugargoo spreadsheet with new verified products, refreshed links, and improved organization.',
        author: { '@type': 'Organization', name: 'Sugargoo Spreadsheet Watch' },
        datePublished: '2026-06-15',
        dateModified: '2026-06-18',
        publisher: { '@type': 'Organization', name: 'Sugargoo Spreadsheet Watch' },
      }}
    >
      <section className="mb-12">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">What Is New in the June 2026 Update?</h2>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
          The June 2026 update to the Sugargoo spreadsheet is one of the most substantial refreshes we have ever released. Over 150 new product links have been added across all categories, with particular emphasis on summer-ready items including lightweight jackets, shorts, sandals, and seasonal accessories. Every existing link has been re-verified, and approximately 8% of outdated links have been replaced with fresh alternatives from trusted sellers.
        </p>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
          Beyond the link refresh, the June update introduces several organizational improvements. Categories now feature sub-sections for easier browsing, and a new &ldquo;Trending Now&rdquo; section highlights the most popular items based on community engagement over the past 30 days.
        </p>

        <div className="bg-accent-50 border border-accent-200/50 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
              <i className="ri-flashlight-line text-xl text-accent-600"></i>
            </div>
            <div>
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">June 2026 Highlights</h3>
              <ul className="text-sm text-foreground-600 leading-relaxed space-y-1">
                <li>&bull; 150+ new verified product links added</li>
                <li>&bull; Summer collection featured prominently</li>
                <li>&bull; 8% outdated links replaced with fresh alternatives</li>
                <li>&bull; New sub-category organization system</li>
                <li>&bull; Trending products section based on community data</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">New Products by Category</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200">
                <th className="text-left py-3 px-4 font-semibold text-foreground-900">Category</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground-900">New Items</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground-900">Hot Picks</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground-900">Shop</th>
              </tr>
            </thead>
            <tbody>
              {[
                { cat: 'Shoes', items: 35, picks: 'Summer Sneakers, Slides', url: 'https://kaspreadsheet.com/category-911-Shoes.html' },
                { cat: 'T-Shirts', items: 42, picks: 'Graphic Tees, Basics', url: 'https://kaspreadsheet.com/category-913-T-Shirts.html' },
                { cat: 'Shorts/Pants', items: 28, picks: 'Cargo, Denim Shorts', url: 'https://kaspreadsheet.com/category-915-Pants%2FShorts.html' },
                { cat: 'Accessories', items: 25, picks: 'Sunglasses, Caps', url: 'https://kaspreadsheet.com/category-920-Accessories.html' },
                { cat: 'Jersey', items: 18, picks: 'Football, Basketball', url: 'https://kaspreadsheet.com/category-919-Jersey.html' },
              ].map((row) => (
                <tr key={row.cat} className="border-b border-background-100 hover:bg-background-100/50 transition-colors">
                  <td className="py-3 px-4 font-medium text-foreground-900">{row.cat}</td>
                  <td className="py-3 px-4 text-foreground-600">{row.items}</td>
                  <td className="py-3 px-4 text-foreground-600">{row.picks}</td>
                  <td className="py-3 px-4"><a href={row.url} target="_blank" rel="noopener nofollow" className="text-primary-600 font-medium hover:text-primary-700 whitespace-nowrap">Browse <i className="ri-arrow-right-up-line text-xs"></i></a></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">How to Get the Updated Spreadsheet</h2>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
          Accessing the latest version of the Sugargoo spreadsheet is straightforward. Simply visit the main spreadsheet link, and you will automatically see the most current version with all June 2026 updates applied. If you have bookmarked an older version, we strongly recommend updating your bookmark to ensure you are always viewing the freshest content. The spreadsheet is a living document that evolves continuously, and staying on the latest version means you never miss out on new finds or risk clicking dead links from outdated versions.
        </p>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
          For the most reliable experience, we recommend checking back at least once a week. The curation team adds new products approximately every 2-3 days, so frequent visitors are always the first to discover hidden gems before they sell out or links expire.
        </p>
      </section>
    </ArticleLayout>
  );
}